import React from 'react';
import { PlayerAvatar } from '../PlayerAvatar';
import ClickablePseudo from '../ClickablePseudo';
import ChatMessageShell from './ChatMessageShell';
import MessageQuoteBlock from './MessageQuoteBlock';
import MessageReactions from './MessageReactions';
import GameInviteMessage from './GameInviteMessage';
import ChatMessageContent from './ChatMessageContent';
import ChatModActions from './ChatModActions';
import type { GlobalChatMessage } from '../../contexts/AppContext';
import type { MessageReaction, ReplyTarget } from '../../types/chat';
import type { DiffereNavigationRequest, LibraryNavigationRequest } from '../../types/app';
import { formatChatTime, getGlobalMessageId } from '../../utils/chatMessage';
import { formatChatMessagePreview, parseChatDeepLinks } from '../../utils/chatDeepLinks';

export interface GlobalChatFeedProps {
  messages: GlobalChatMessage[];
  currentPseudo: string;
  reactions: Record<string, MessageReaction[]>;
  onToggleReaction: (messageId: string, emoji: string) => void;
  onReply: (target: ReplyTarget) => void;
  onReport?: (message: GlobalChatMessage) => void;
  canModerate?: boolean;
  onModerationDone?: (toast: string) => void;
  onRespondToGameInvite: (messageId: string, accepted: boolean) => void | Promise<void>;
  onCancelGameInvite: (messageId: string) => Promise<unknown>;
  onAccessDraft: (draftId: string, gameMode?: string) => void;
  onOpenLibrary?: (request: LibraryNavigationRequest) => void;
  onOpenDiffere?: (request: DiffereNavigationRequest) => void;
  onScrollToQuote: (messageId: string) => void;
  enableMessageShell?: boolean;
  enableSwipe?: boolean;
  messagesEndRef?: React.RefObject<HTMLDivElement | null>;
}

export default function GlobalChatFeed({
  messages,
  currentPseudo,
  reactions,
  onToggleReaction,
  onReply,
  onReport,
  canModerate = false,
  onModerationDone,
  onRespondToGameInvite,
  onCancelGameInvite,
  onAccessDraft,
  onOpenLibrary,
  onOpenDiffere,
  onScrollToQuote,
  enableMessageShell = true,
  enableSwipe = false,
  messagesEndRef,
}: GlobalChatFeedProps) {
  if (messages.length === 0) {
    return (
      <div className="messages-empty">
        <p>💬</p>
        <p>Aucun message pour le moment</p>
        <p className="small">Soyez le premier à parler !</p>
      </div>
    );
  }

  return (
    <>
      {messages.map((msg, index) => {
        const isOwn = msg.pseudo.toLowerCase() === currentPseudo.toLowerCase();
        const showAuthor = !isOwn && (index === 0 || messages[index - 1].pseudo !== msg.pseudo);
        const messageId = getGlobalMessageId(msg);
        const replyTarget: ReplyTarget = {
          id: messageId,
          author: msg.pseudo,
          text: formatChatMessagePreview(msg.text),
        };
        const messageReactions = reactions[messageId] ?? msg.reactions ?? [];
        const isGameInvite = msg.messageType === 'game_invite' && !!msg.gameInvite;
        const deepLinkPresentation = parseChatDeepLinks(msg.text);
        const deepLinkCardsOnly = deepLinkPresentation.cards.length > 0 && !deepLinkPresentation.displayText;

        const messageContent = (
          <div className={`message message-interactive ${isOwn ? 'outgoing' : 'incoming'}`}>
            {!isOwn && showAuthor && !isGameInvite && (
              <PlayerAvatar pseudo={msg.pseudo} size={32} className="message-avatar" />
            )}
            {!isOwn && !showAuthor && !isGameInvite && <div className="message-avatar-placeholder" />}
            <div className="message-bubble-wrap">
              <div className={`message-bubble ${isGameInvite || deepLinkCardsOnly ? 'game-invite-bubble' : ''}`}>
                {!isOwn && showAuthor && !isGameInvite && (
                  <div className="message-author">
                    <ClickablePseudo pseudo={msg.pseudo}>{msg.pseudo}</ClickablePseudo>
                  </div>
                )}
                {isGameInvite ? (
                  <>
                    <GameInviteMessage
                      invite={msg.gameInvite!}
                      author={msg.pseudo}
                      currentPseudo={currentPseudo}
                      showProposer
                      showCancelledRecipients
                      onRespond={async (accepted) => { await onRespondToGameInvite(messageId, accepted); }}
                      onCancel={async () => { await onCancelGameInvite(messageId); }}
                      onAccess={() => onAccessDraft(
                        msg.gameInvite!.draftId,
                        msg.gameInvite!.config.gameMode,
                      )}
                    />
                    <span className="message-meta game-invite-meta">
                      <span className="message-time">{formatChatTime(msg.timestamp)}</span>
                    </span>
                  </>
                ) : (
                  <>
                    {msg.quote && (
                      <MessageQuoteBlock
                        quote={msg.quote}
                        isOutgoing={isOwn}
                        onClick={() => onScrollToQuote(msg.quote!.id)}
                      />
                    )}
                    <ChatMessageContent
                      text={msg.text}
                      onOpenLibrary={onOpenLibrary ?? (() => {})}
                      onOpenDiffere={onOpenDiffere ?? (() => {})}
                      meta={(
                        <>
                          {msg.source === 'irc' && (
                            <span className="source-badge">IRC</span>
                          )}
                          <span className="message-time">{formatChatTime(msg.timestamp)}</span>
                        </>
                      )}
                    />
                  </>
                )}
              </div>
              {!isGameInvite && (
                <MessageReactions
                  reactions={messageReactions}
                  currentPseudo={currentPseudo}
                  onReact={(emoji) => onToggleReaction(messageId, emoji)}
                />
              )}
            </div>
          </div>
        );

        if (!enableMessageShell || isGameInvite) {
          return <React.Fragment key={messageId}>{messageContent}</React.Fragment>;
        }

        return (
          <ChatMessageShell
            key={messageId}
            messageId={messageId}
            alignRight={isOwn}
            onReply={onReply}
            onReact={(emoji) => onToggleReaction(messageId, emoji)}
            onReport={
              !isOwn && onReport
                ? () => onReport(msg)
                : undefined
            }
            modSlot={
              canModerate && !isOwn ? (
                <ChatModActions
                  staffPseudo={currentPseudo}
                  message={{
                    pseudo: msg.pseudo,
                    roomId: msg.roomId || 'global:detente',
                    messageId,
                    text: msg.text,
                  }}
                  onDone={onModerationDone}
                  align={isOwn ? 'start' : 'end'}
                />
              ) : undefined
            }
            replyTarget={replyTarget}
            enableSwipe={enableSwipe}
          >
            {messageContent}
          </ChatMessageShell>
        );
      })}
      {messagesEndRef && <div ref={messagesEndRef} />}
    </>
  );
}
